import { Form, Input, Modal, Select } from 'antd';
import { useEffect } from 'react';
import type { AccessAppPolicy } from '@/types';

interface Props {
  open: boolean;
  appID: string | null;
  policy: AccessAppPolicy | null;
  onClose: () => void;
  onSave: (
    appID: string,
    policy: AccessAppPolicy,
    previousAppID?: string,
  ) => void;
}

interface FormValues {
  appID: string;
  enabled: 'inherit' | 'true' | 'false';
  protocolsMode: 'inherit' | 'override';
  protocols: string;
  modelsMode: 'inherit' | 'override';
  models: string;
  providersMode: 'inherit' | 'override';
  providers: string;
  routesMode: 'inherit' | 'override';
  routes: string;
}

function lines(values?: string[]): string {
  return (values || []).join('\n');
}

function parseLines(value: string): string[] {
  return value
    .split(/[\n,]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function hasOwnList(
  policy: AccessAppPolicy | null,
  key:
    | 'allowed_protocols'
    | 'allowed_models'
    | 'allowed_providers'
    | 'allowed_routes',
) {
  return policy != null && key in policy;
}

function setList(
  target: AccessAppPolicy,
  key:
    | 'allowed_protocols'
    | 'allowed_models'
    | 'allowed_providers'
    | 'allowed_routes',
  mode: 'inherit' | 'override',
  value: string,
) {
  if (mode === 'inherit') {
    delete target[key];
    return;
  }
  target[key] = parseLines(value);
}

export function AccessAppDialog({
  open,
  appID,
  policy,
  onClose,
  onSave,
}: Props) {
  const [form] = Form.useForm<FormValues>();

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      appID: appID || '',
      enabled:
        policy?.enabled == null ? 'inherit' : policy.enabled ? 'true' : 'false',
      protocolsMode: hasOwnList(policy, 'allowed_protocols')
        ? 'override'
        : 'inherit',
      protocols: lines(policy?.allowed_protocols),
      modelsMode: hasOwnList(policy, 'allowed_models') ? 'override' : 'inherit',
      models: lines(policy?.allowed_models),
      providersMode: hasOwnList(policy, 'allowed_providers')
        ? 'override'
        : 'inherit',
      providers: lines(policy?.allowed_providers),
      routesMode: hasOwnList(policy, 'allowed_routes') ? 'override' : 'inherit',
      routes: lines(policy?.allowed_routes),
    });
  }, [appID, form, open, policy]);

  const confirm = async () => {
    const values = await form.validateFields();
    const next: AccessAppPolicy = {
      ...(policy || {}),
    };
    setList(next, 'allowed_protocols', values.protocolsMode, values.protocols);
    setList(next, 'allowed_models', values.modelsMode, values.models);
    setList(next, 'allowed_providers', values.providersMode, values.providers);
    setList(next, 'allowed_routes', values.routesMode, values.routes);
    if (values.enabled === 'inherit') {
      delete next.enabled;
    } else {
      next.enabled = values.enabled === 'true';
    }
    onSave(values.appID.trim(), next, appID || undefined);
  };

  return (
    <Modal
      open={open}
      title={appID ? `编辑 App ${appID}` : '新增 App Policy'}
      width={680}
      destroyOnClose
      onCancel={onClose}
      onOk={() => void confirm()}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="appID"
          label="App ID"
          rules={[{ required: true, message: '请输入 App ID' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item name="enabled" label="Enabled">
          <Select
            options={[
              { label: '继承 Defaults', value: 'inherit' },
              { label: '启用', value: 'true' },
              { label: '停用', value: 'false' },
            ]}
          />
        </Form.Item>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Form.Item name="protocolsMode" label="Allowed Protocols">
              <Select
                options={[
                  { label: '继承 Defaults', value: 'inherit' },
                  { label: 'App 覆盖', value: 'override' },
                ]}
              />
            </Form.Item>
            <Form.Item name="protocols" label="值（一行一个）">
              <Input.TextArea rows={4} />
            </Form.Item>
          </div>
          <div>
            <Form.Item name="modelsMode" label="Allowed Models / glob">
              <Select
                options={[
                  { label: '继承 Defaults', value: 'inherit' },
                  { label: 'App 覆盖', value: 'override' },
                ]}
              />
            </Form.Item>
            <Form.Item name="models" label="值（一行一个）">
              <Input.TextArea rows={4} />
            </Form.Item>
          </div>
          <div>
            <Form.Item name="providersMode" label="Allowed Providers / glob">
              <Select
                options={[
                  { label: '继承 Defaults', value: 'inherit' },
                  { label: 'App 覆盖', value: 'override' },
                ]}
              />
            </Form.Item>
            <Form.Item name="providers" label="值（一行一个）">
              <Input.TextArea rows={4} />
            </Form.Item>
          </div>
          <div>
            <Form.Item name="routesMode" label="Allowed Routes / glob">
              <Select
                options={[
                  { label: '继承 Defaults', value: 'inherit' },
                  { label: 'App 覆盖', value: 'override' },
                ]}
              />
            </Form.Item>
            <Form.Item name="routes" label="值（一行一个）">
              <Input.TextArea rows={4} />
            </Form.Item>
          </div>
        </div>
        <div className="text-xs text-muted-foreground">
          User Override、Quota 和 Rate Limit 保留原值，本期不在此处编辑。
        </div>
      </Form>
    </Modal>
  );
}
